import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/auth-store";
import {
  getAuthorizedApps,
  getNavItems,
  getAppByPath,
  PermissionChecker,
  getUniqueEntitiesFromPermissions,
} from "@/lib/utils";
import { App, EntityInstanceDto, NavItem } from "@/lib/types";

interface AppContextValue {
  currentApp: App | null;
  setCurrentApp: (app: App) => void;
  apps: App[];
  navItems: NavItem[];
  entities: EntityInstanceDto[];
  actions: string | string[];
  selectedEntity: EntityInstanceDto | null;
  handleEntityChange: (entityInstance: EntityInstanceDto) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { user, isAuthenticated, hasEntityAction, isInGroup } = useAuthStore();

  const permissionChecker: PermissionChecker = useMemo(
    () => ({ hasEntityAction, isInGroup }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasEntityAction, isInGroup, user?.permissions, user?.groups] // Depend on actual permission data
  );

  const authorizedApps = useMemo(
    () => (isAuthenticated ? getAuthorizedApps(permissionChecker) : []),
    [isAuthenticated, permissionChecker]
  );

  const [currentApp, setCurrentApp] = useState<App | null>(() =>
    getAppByPath(location.pathname, authorizedApps)
  );

  const entities = useMemo(() => {
    if (!user?.permissions) return [];
    return getUniqueEntitiesFromPermissions(user.permissions);
  }, [user?.permissions]);

  const actions = useMemo(() => {
    if (!user?.permissions) return [];
    return Array.from(
      new Set(user.permissions.flatMap((p) => p.actions || []))
    );
  }, [user?.permissions]);

  console.log("🔍 User actions in AppProvider:", actions);

  const [selectedEntity, setSelectedEntity] = useState<EntityInstanceDto | null>(() => {
    // Try to load from localStorage on initialization
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('selectedEntity');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          return null;
        }
      }
    }
    return null;
  });

  // Set first available entity as default when entities load (only if no saved entity)
  useEffect(() => {
    if (entities.length > 0 && !selectedEntity) {
      setSelectedEntity(entities[0]);
    }
  }, [entities, selectedEntity]);

  // Handle entity selection
  const handleEntityChange = (entity: EntityInstanceDto) => {
    // Store in localStorage for persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedEntity', JSON.stringify(entity));
    }
    setSelectedEntity(entity);
  };

  // Update current app when location changes
  useEffect(() => {
    const app = getAppByPath(location.pathname, authorizedApps);
    if (app !== currentApp) {
      setCurrentApp(app);
    }
  }, [location.pathname, authorizedApps, currentApp]);

  const navItems = useMemo(
    () => (currentApp ? getNavItems(currentApp, permissionChecker) : []),
    [currentApp, permissionChecker]
  );

  return (
    <AppContext.Provider
      value={{
        currentApp,
        setCurrentApp,
        apps: authorizedApps,
        entities,
        navItems,
        selectedEntity,
        handleEntityChange,
        actions,
      }}
    >
      {children}
    </AppContext.Provider>

  );
};


export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
};
